import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { TREATMENT_VIDEOS, buildWhatsAppUrl } from "@/lib/site";
import { treatmentsCatalog } from "@/data/catalog";
import { ArrowUpRight, Check, Clock, Calendar } from "lucide-react";
import SmartBgVideo from "@/components/SmartBgVideo";

const Tratamientos = () => {
  const { t, lang } = useLang();
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [hash]);

  return (
    <main data-testid="treatments-page" className="bg-cream text-ink">
      {/* HERO */}
      <section className="pt-40 pb-12 md:pt-48 md:pb-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-7">
            <p className="overline">{t.treatments_page.eyebrow}</p>
            <h1 className="mt-6 font-serif text-6xl md:text-8xl font-light tracking-tight leading-[0.95] whitespace-pre-line">
              {t.treatments_page.title}
            </h1>
          </div>
          <div className="md:col-span-5 md:pl-8">
            <p className="text-ink/75 font-light text-lg leading-relaxed max-w-md">{t.treatments_page.lede}</p>
            <a
              href={buildWhatsAppUrl("first_visit", lang)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="treatments-page-cta"
              className="inline-flex items-center gap-3 mt-8 px-6 py-3 border border-ink text-xs tracking-[0.25em] uppercase btn-sweep hover:text-cream"
            >
              {t.hero.cta_primary}
              <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* JUMP NAV */}
      <section className="pb-8 border-t border-ink/15">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-8 flex flex-wrap gap-x-6 gap-y-2">
          <span className="overline text-ink/50 mr-2 self-center">{t.treatments_page.jump}:</span>
          {treatmentsCatalog.map((tr, i) => (
            <a
              key={tr.key}
              href={`#${tr.key}`}
              data-testid={`jump-${tr.key}`}
              className="text-sm font-light link-underline"
            >
              {String(i + 1).padStart(2, "0")} · {tr[lang].name}
            </a>
          ))}
        </div>
      </section>

      {/* LIST */}
      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {treatmentsCatalog.map((tr, i) => {
            const d = tr[lang];
            const reversed = i % 2 === 1;
            const videoSrc = TREATMENT_VIDEOS[tr.key];
            return (
              <article
                key={tr.key}
                id={tr.key}
                data-testid={`treatment-row-${tr.key}`}
                className={`grid md:grid-cols-12 gap-8 md:gap-16 py-20 border-t border-ink/15 items-center scroll-mt-28 ${
                  reversed ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:col-span-6">
                  <div className="aspect-[4/3] bg-ink overflow-hidden relative">
                    {videoSrc ? (
                      <SmartBgVideo
                        src={videoSrc}
                        poster={tr.img}
                        alt={d.name}
                        className="absolute inset-0 w-full h-full"
                        imgClassName="hover:scale-105 transition-transform duration-700"
                        videoClassName="hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <img
                        src={tr.img}
                        alt={d.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      />
                    )}
                  </div>
                </div>
                <div className="md:col-span-6">
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif text-xl text-ink/40 italic">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <p className="overline">{d.sub}</p>
                  </div>
                  <h2 className="mt-4 font-serif text-4xl md:text-5xl font-light tracking-tight leading-tight">
                    {d.name}
                  </h2>
                  <p className="mt-5 text-ink/75 font-light leading-relaxed max-w-xl">{d.lede}</p>

                  <div className="mt-7 grid grid-cols-2 gap-6 max-w-md border-y border-ink/15 py-5">
                    <div className="flex items-start gap-3">
                      <Clock size={18} strokeWidth={1.4} className="mt-0.5" />
                      <div>
                        <p className="overline text-ink/55">{t.treatments.duration}</p>
                        <p className="font-serif text-lg mt-1">{d.duration}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Calendar size={18} strokeWidth={1.4} className="mt-0.5" />
                      <div>
                        <p className="overline text-ink/55">{t.treatments.sessions}</p>
                        <p className="font-serif text-lg mt-1">{d.sessions}</p>
                      </div>
                    </div>
                  </div>

                  <p className="overline text-ink/55 mt-6 mb-3">{t.treatments.benefits}</p>
                  <ul className="space-y-2.5">
                    {d.points.map((p, k) => (
                      <li key={k} className="flex items-start gap-3 font-light text-ink/85">
                        <Check size={16} strokeWidth={1.5} className="mt-1 shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 flex flex-wrap gap-4">
                    <a
                      href={buildWhatsAppUrl("treatment", lang, d.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`treatment-cta-${tr.key}`}
                      className="px-5 py-3 bg-ink text-cream text-xs tracking-[0.25em] uppercase btn-sweep sweep-cream border border-ink hover:text-ink"
                    >
                      {t.treatments.cta_book}
                    </a>
                    <Link
                      to="/contacto"
                      data-testid={`treatment-info-${tr.key}`}
                      className="px-5 py-3 border border-ink text-xs tracking-[0.25em] uppercase btn-sweep hover:text-cream"
                    >
                      {t.treatments.cta_info}
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </main>
  );
};

export default Tratamientos;
